<template>
  <div class="chatbot-root">
    <div
      v-show="chatBotStore.isChatOpen"
      id="chatbot-window"
      ref="chatWindowEl"
      :class="{ 'full-height': chatBotStore.fullHeight, 'chat-dragging': isDragging }"
      :style="chatWindowStyle"
      class="chat-history chat-wrapper"
    >
      <div :class="{ 'chat-drag-handle': !chatBotStore.fullHeight }" @pointerdown="onDragStart">
        <chat-header />
      </div>

      <accept-terms v-if="!chatBotStore.acceptedTerms" />
      <chat-window v-else />
    </div>
    <button
      v-if="!chatBotStore.isChatOpen"
      class="chat-toggle-btn"
      title="Open chat bot"
      @click="chatBotStore.toggleChat()"
    >
      <img src="../svg/chat-bubble.svg" alt="ChatBubble" />
      Get help
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useChatBotStore } from "../stores/chatBotStore";
import ChatHeader from "./header.vue";
import ChatWindow from "./chatWindow.vue";
import AcceptTerms from "./acceptTerms.vue";

const chatBotStore = useChatBotStore();

const chatWindowEl = ref<HTMLElement | null>(null);
const dragOffsetX = ref(0);
const dragOffsetY = ref(0);
const isDragging = ref(false);

let dragStartX = 0;
let dragStartY = 0;
let offsetStartX = 0;
let offsetStartY = 0;

const chatWindowStyle = computed(() => {
  if (chatBotStore.fullHeight) {
    return undefined;
  }

  return {
    transform: `translate(${dragOffsetX.value}px, ${dragOffsetY.value}px)`,
  };
});

function onDragStart(event: PointerEvent) {
  if (chatBotStore.fullHeight) {
    return;
  }

  const target = event.target as HTMLElement | null;
  if (target?.closest("button, input, textarea, select, a, label")) {
    return;
  }

  isDragging.value = true;
  dragStartX = event.clientX;
  dragStartY = event.clientY;
  offsetStartX = dragOffsetX.value;
  offsetStartY = dragOffsetY.value;

  document.body.style.userSelect = "none";
  window.addEventListener("pointermove", onDragMove);
  window.addEventListener("pointerup", onDragEnd);
}

function onDragMove(event: PointerEvent) {
  if (!isDragging.value) {
    return;
  }

  const newX = offsetStartX + (event.clientX - dragStartX);
  const newY = offsetStartY + (event.clientY - dragStartY);

  if (chatWindowEl.value) {
    const rect = chatWindowEl.value.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // The element's current position without the new offset applied
    const baseLeft = rect.left - dragOffsetX.value;
    const baseTop = rect.top - dragOffsetY.value;

    const minX = -baseLeft;
    const maxX = vw - baseLeft - rect.width;
    const minY = -baseTop;
    const maxY = vh - baseTop - rect.height;

    dragOffsetX.value = Math.min(Math.max(newX, minX), maxX);
    dragOffsetY.value = Math.min(Math.max(newY, minY), maxY);
  } else {
    dragOffsetX.value = newX;
    dragOffsetY.value = newY;
  }
}

function onDragEnd() {
  isDragging.value = false;
  document.body.style.userSelect = "";
  window.removeEventListener("pointermove", onDragMove);
  window.removeEventListener("pointerup", onDragEnd);
}

watch(
  () => chatBotStore.fullHeight,
  isFullHeight => {
    if (isFullHeight) {
      dragOffsetX.value = 0;
      dragOffsetY.value = 0;
      onDragEnd();
    }
  }
);

onBeforeUnmount(() => {
  onDragEnd();
});
</script>

<style lang="css">
@import "../styles/chatBot.css";
</style>
